import { TranslateService } from '@ngx-translate/core';

import { CmpsDatePipe } from '../../../common/date.pipe';
import { CmpsDecimalPipe, CmpsPercentPipe } from '../../../common/number.pipe';
import { CellNode } from '../../../insight/experimental.grid';
import { Pillar, PillarMetric } from '../../core/definition.scorecard.model';
import { CmpsNumberWithSign } from '../../numberWithSign.pipe';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { PillarAndMetricResult } from '../../scorecard.model';

// must extend OptionalColumnType
export enum ColumnType {
    ACTUAL,
    TARGET,
    PERCENT_TO_TARGET,
    WEIGHT,
    ORIGINAL_POINTS,
    ADJUSTED_POINTS,
    METRIC_SCORE,
    METRIC_RANK,
    POINTS_BAND,
    N_TILE,
    N_TILE_BAND,
    ACTUAL_TYPE,
    TARGET_TYPE,
    PERCENT_TYPE,
    SCORING_TYPE,
    ACTUAL_TREND,
    PERCENT_TO_TARGET_TREND,
    COACHING_PLAN_ACTIVE,
    METRIC_LABEL,

    // internal
    PILLAR = 9000,
    METRIC_NAME,
}

export enum TemplateID {
    HEADER = 'header',
    CONTENT = 'content',
}

export interface ColumnContext {
    templateId: TemplateID;
    type: ColumnType;
    nameOverride?: string;
}

export interface RowContext {
    isLastRowOfPillar?: boolean;
}

export interface CellContext {
    templateId: TemplateID;
    pillar: Pillar;
    metric: PillarMetric;
    data: PillarAndMetricResult;
}

const initialValue = '-';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function checkNotNull(metric: number | string) {
    return metric != null;
}

function getTypeValues(value: string): string {
    return value || initialValue;
}

function getYesNoValues(
    value: boolean,
    translateService: TranslateService,
): string {
    if (value) {
        return translateService.instant(TranslationKeyMap._9297_YES);
    }
    return translateService.instant(TranslationKeyMap._9298_NO);
}

function getPillarValue(columnType: ColumnType, metric: CellContext): string {
    let val = initialValue;
    if (columnType === ColumnType.PILLAR) {
        val = metric.pillar.name;
    }
    return val;
}

function getMetricValue(columnType: ColumnType, metric: CellContext): string {
    let val = initialValue;
    if (columnType === ColumnType.METRIC_NAME) {
        val =
            metric.data.metricName ||
            metric.data.metricShortName ||
            metric.data.metricInitials;
    }
    return val;
}

function getActualValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.actual)) {
        val = decimalPipe.transform(metric.data.actual, '1.2-2');
    }
    return val;
}

function getTargetValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.target)) {
        val = decimalPipe.transform(metric.data.target, '1.2-2');
    }
    return val;
}

function getPercentToTargetValue(
    metric: CellContext,
    percentPipe: CmpsPercentPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.targetPercent)) {
        val = percentPipe.transform(metric.data.targetPercent, '1.2-2');
    }
    return val;
}

function getWeightValue(
    metric: CellContext,
    percentPipe: CmpsPercentPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.weight)) {
        val = percentPipe.transform(metric.data.weight, '1.2-2');
    }
    return val;
}

function getOriginalPointsValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.originalPoints)) {
        val = decimalPipe.transform(metric.data.originalPoints, '1.2-2');
    }
    return val;
}

function getAdjustedPointsValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.adjustedPoints)) {
        val = decimalPipe.transform(metric.data.adjustedPoints, '1.2-2');
    }
    return val;
}

function getMetricScoreValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.metricScore)) {
        val = decimalPipe.transform(metric.data.metricScore, '1.2-2');
    }
    return val;
}

function getMetricRankSgValue(metric: CellContext): string {
    let val = initialValue;
    if (checkNotNull(metric.data.metricRankSg)) {
        val = metric.data.metricRankSg;
    }
    return val;
}

function getNTileValue(
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val = initialValue;
    if (checkNotNull(metric.data.nTile)) {
        val = decimalPipe.transform(metric.data.nTile, '1.2-2');
    }
    return val;
}

function getMetricLabelValue(): string {
    return initialValue;
}

export function valueForColumn(
    columnType: ColumnType,
    metric: CellContext,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    dataPipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
): string {
    let val;

    switch (columnType) {
        case ColumnType.PILLAR:
            val = getPillarValue(columnType, metric);
            break;
        case ColumnType.METRIC_NAME:
            val = getMetricValue(columnType, metric);
            break;
        case ColumnType.ACTUAL:
            val = getActualValue(metric, decimalPipe);
            break;
        case ColumnType.TARGET:
            val = getTargetValue(metric, decimalPipe);
            break;
        case ColumnType.PERCENT_TO_TARGET:
            val = getPercentToTargetValue(metric, percentPipe);
            break;
        case ColumnType.WEIGHT:
            val = getWeightValue(metric, percentPipe);
            break;
        case ColumnType.ORIGINAL_POINTS:
            val = getOriginalPointsValue(metric, decimalPipe);
            break;
        case ColumnType.ADJUSTED_POINTS:
            val = getAdjustedPointsValue(metric, decimalPipe);
            break;
        case ColumnType.METRIC_SCORE:
            val = getMetricScoreValue(metric, decimalPipe);
            break;
        case ColumnType.METRIC_RANK:
            val = getMetricRankSgValue(metric);
            break;
        case ColumnType.POINTS_BAND:
            val = metric.data.pointsBand;
            break;
        case ColumnType.N_TILE:
            val = getNTileValue(metric, decimalPipe);
            break;
        case ColumnType.N_TILE_BAND:
            val = getTypeValues(metric.data.nTileBand);
            break;
        case ColumnType.ACTUAL_TYPE:
            val = getTypeValues(metric.data.actualType);
            break;
        case ColumnType.TARGET_TYPE:
            val = getTypeValues(metric.data.targetType);
            break;
        case ColumnType.PERCENT_TYPE:
            val = getTypeValues(metric.data.percentType);
            break;
        case ColumnType.SCORING_TYPE:
            val = getTypeValues(metric.data.scoreType);
            break;
        case ColumnType.ACTUAL_TREND:
            val = numberWithSignPipe.transform(
                metric.data.actualTrend,
                'p',
                '1.2-2',
                true,
                '+',
            );
            break;
        case ColumnType.PERCENT_TO_TARGET_TREND:
            val = numberWithSignPipe.transform(
                metric.data.targetPercentTrend,
                'p',
                '1.2-2',
                true,
                '+',
            );
            break;
        case ColumnType.COACHING_PLAN_ACTIVE:
            val = getYesNoValues(
                metric.data.coachingPlanActive,
                translateService,
            );
            break;
        case ColumnType.METRIC_LABEL:
            val = getMetricLabelValue();
            break;
    }

    return val;
}

function getHeaderValue(
    columnType: ColumnType,
    translateService: TranslateService,
    nameOverride: any,
): string {
    if (columnType === ColumnType.PILLAR) {
        return translateService.instant(TranslationKeyMap._9255_PILLAR);
    }

    if (columnType === ColumnType.METRIC_NAME) {
        return translateService.instant(TranslationKeyMap._7146_METRIC_NAME);
    }

    if (nameOverride) {
        return nameOverride;
    }

    switch (columnType) {
        case ColumnType.ACTUAL: {
            return translateService.instant(TranslationKeyMap._725_ACTUAL);
        }
        case ColumnType.TARGET: {
            return translateService.instant(TranslationKeyMap._726_TARGET);
        }
        case ColumnType.PERCENT_TO_TARGET: {
            return translateService.instant(TranslationKeyMap._768___TO_TARGET);
        }
        case ColumnType.WEIGHT: {
            return translateService.instant(TranslationKeyMap._9256_WEIGHT);
        }
        case ColumnType.ORIGINAL_POINTS: {
            return translateService.instant(
                TranslationKeyMap._9257_ORIGINAL_POINTS,
            );
        }
        case ColumnType.ADJUSTED_POINTS: {
            return translateService.instant(
                TranslationKeyMap._9258_ADJUSTED_POINTS,
            );
        }
        case ColumnType.METRIC_SCORE: {
            return translateService.instant(
                TranslationKeyMap._9259_METRIC_SCORE,
            );
        }
        case ColumnType.METRIC_RANK: {
            return translateService.instant(
                TranslationKeyMap._9260_METRIC_RANK,
            );
        }
        case ColumnType.POINTS_BAND: {
            return translateService.instant(
                TranslationKeyMap._9261_POINTS_BAND,
            );
        }
        case ColumnType.N_TILE: {
            return translateService.instant(TranslationKeyMap._9262_N_TILE);
        }
        case ColumnType.N_TILE_BAND: {
            return translateService.instant(
                TranslationKeyMap._9263_N_TILE_BAND,
            );
        }
        case ColumnType.ACTUAL_TYPE: {
            return translateService.instant(
                TranslationKeyMap._9264_ACTUAL_TYPE,
            );
        }
        case ColumnType.TARGET_TYPE: {
            return translateService.instant(
                TranslationKeyMap._9265_TARGET_TYPE,
            );
        }
        case ColumnType.PERCENT_TYPE: {
            return translateService.instant(
                TranslationKeyMap._9266_PERCENT_TYPE,
            );
        }
        case ColumnType.SCORING_TYPE: {
            return translateService.instant(
                TranslationKeyMap._9267_SCORING_TYPE,
            );
        }
        case ColumnType.ACTUAL_TREND: {
            return translateService.instant(
                TranslationKeyMap._9268_ACTUAL_TREND,
            );
        }
        case ColumnType.PERCENT_TO_TARGET_TREND: {
            return translateService.instant(
                TranslationKeyMap._9269___TO_TARGET_TREND,
            );
        }
        case ColumnType.COACHING_PLAN_ACTIVE: {
            return translateService.instant(
                TranslationKeyMap._9270_COACHING_PLAN_ACTIVE,
            );
        }
        case ColumnType.METRIC_LABEL: {
            return translateService.instant(
                TranslationKeyMap._9271_METRIC_LABEL,
            );
        }
        default:
            return null;
    }
}

export function exportGetValue(
    l: CellNode,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    datePipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
): any {
    const metric = l.context.ctx;
    const columnType = l.context.c.ctx.type as ColumnType;
    const nameOverride = l.context.c.ctx.nameOverride;

    let val;

    if (metric.templateId === 'header') {
        val = getHeaderValue(columnType, translateService, nameOverride);
    } else {
        val = valueForColumn(
            columnType,
            metric,
            translateService,
            decimalPipe,
            percentPipe,
            datePipe,
            numberWithSignPipe,
        );
    }

    return val;
}
